import React, { useEffect } from 'react';
import type { Draft } from 'immer';
import {
  AutoRotateAction,
  AutoRotateType,
  type SceneConfigAutoRotate,
  SceneType,
} from '@types';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { Collapse } from '@components/Collapse';
import { $sceneMapApi } from '@stores/dataStores';
import Slider from '@components/Slider';
import { MainPlayer } from '@stores/krpPlayerStores';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { sendSuccess } from '@components/Noti';
import { showSceneModal } from '@modules/category';
import type { SidebarAutoRotateProps } from './types';

const typeItems: DropdownItem<AutoRotateType>[] = [
  { id: AutoRotateType.right, name: '向右旋转' },
  { id: AutoRotateType.left, name: '向左旋转' },
  { id: AutoRotateType.none, name: '不旋转' },
];

export function SidebarAutoRotatePVideo({
  sceneId,
  data,
}: SidebarAutoRotateProps) {
  const { type, duration, holdView } = data;

  useEffect(() => {
    MainPlayer.get()?.updateAutoRotate({
      horizon: 'off',
      tofov: 'off',
    });
    if (type === AutoRotateType.none) {
      MainPlayer.get()?.autoRotateStop();
    } else {
      let dur = duration ?? 0;
      if (type === AutoRotateType.left) dur = -dur;
      MainPlayer.get()?.autoRotateStart(dur);
    }
    return () => {
      MainPlayer.get()?.autoRotateStop();
    };
  }, [type, duration, holdView]);

  const updateAutoRotate = (
    updater: (
      draft: Draft<SceneConfigAutoRotate>
    ) => SceneConfigAutoRotate | void
  ) => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        updater(draft.config.autoRotate!);
      },
    });
  };

  const handleTypeChange = (typeId: AutoRotateType) => {
    updateAutoRotate((draft) => {
      draft.type = typeId;
      // 向左旋转/向右旋转与不旋转切换时，下面参数恢复对应默认值。
      if (typeId === AutoRotateType.none || type === AutoRotateType.none) {
        draft.duration = 180;
        draft.stayTime = 180;
        draft.holdView = 1;
        draft.rotateAction = AutoRotateAction.byRound;
      }
    });
  };

  const handleDurChange = (val: number) => {
    updateAutoRotate((draft) => {
      draft.duration = val;
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [SceneType.panoVideo],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.config.autoRotate = {
          ...draft.get(_id)!.config.autoRotate,
          type,
          duration,
          holdView,
          rotateAction:
            type === AutoRotateType.none ? AutoRotateAction.byRound : undefined,
        };
      });
    });
    sendSuccess('导览方式已应用到所选场景');
  };

  return (
    <Collapse title="导览方式">
      <Dropdown selected={type} items={typeItems} onChange={handleTypeChange} />
      {type !== AutoRotateType.none && (
        <Slider
          className="mt-20"
          min={30}
          max={360}
          valToString={(val) => `${val}秒/圈`}
          value={duration}
          onChange={handleDurChange}
        >
          <Slider.Header title="旋转速度" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
      )}

      <SidebarApplyTo
        title="把导览方式应用到..."
        className="mt-20"
        onClick={handleApplyTo}
      />
    </Collapse>
  );
}
